import { useEffect, useMemo, useState } from "react"
import _ from "underscore";
import services from "../../../services/services";
import { decryptData, encryptData } from "../../../utils/encrypt";

export const useDatabaseSize = ({ projectChanged }) => {

    const [databaseData, setDatabaseData] = useState({ x_axis: [], y_axis: { used: [], available: [] }, api_data: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOpts, setFilterOpts] = useState({ dbname: [] });
    const [filterValues, setFilterValues] = useState({ dbname: [] });
    const [filterCount, setFilterCount] = useState(0);
    const [expand, setExpand] = useState(false);
    const [intervalFlag, setIntervalFlag] = useState(false);

    useEffect(() => {
        getData();
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            getData();
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    const getData = async () => {
        try {
            setDatabaseData({ x_axis: [], y_axis: { used: [], available: [] }, api_data: [] });
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject) && selectedProject?.host) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("dbsizefilters")) || "{}");
                const payload = {
                    type: 'error',
                    dbname: _.isEmpty(filters?.dbname) ? undefined : filters?.dbname?.toString(),
                    project_name: selectedProject?.project_key
                }
                const hostArray = _.pluck(selectedProject?.host, "domain_host_key");
                const responses = await Promise.all(
                    hostArray.map(async (host) => {
                        try {
                            const response = await services.callInspectra("GET", '/inspectra/dbspace/v1', payload, host);
                            return response?.data?.[0];
                        } catch (err) {
                            console.error(`Error fetching data for host ${host}`, err);
                            return null;
                        }
                    })
                );

                const dataArray = responses.filter(Boolean).flat();

                if (dataArray) {
                    const data = dataArray;
                    const database = _.uniq(_.pluck(data, "database_name"));
                    _.isEmpty(filters?.dbname) && setFilterOpts((prev) => ({ ...prev, dbname: database }));
                    let data_file = [];
                    let log_file = [];
                    let total = [];
                    database?.forEach((item) => {
                        data_file.push(parseFloat(_.findWhere(data, { database_name: item })?.db_row_size_mb));
                        log_file.push(parseFloat(_.findWhere(data, { database_name: item })?.db_log_size_mb));
                        total.push(parseFloat(_.findWhere(data, { database_name: item })?.db_total_size_mb));
                    })
                    setDatabaseData({ x_axis: database, y_axis: { data_file: data_file, log_file: log_file, total: total }, api_data: data });
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader(false);
        }
    }

    const options = {
        credits: { enabled: false },
        chart: {
            type: 'column',
            events: {
                render: function () {
                    const chart = this;
                    chart.xAxis[0].ticks &&
                        Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label && !tick.label.clickAttached) {
                                tick.label.element.style.cursor = 'pointer';
                                tick.label.element.onclick = () => {
                                    handleShowDetailsPopup(tick.label.textStr);
                                };
                                tick.label.clickAttached = true;
                            }
                        });
                }
            }
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: databaseData?.x_axis,
            gridLineDashStyle: 'solid',
            gridLineWidth: 1
        },
        yAxis: {
            type: 'logarithmic',
            title: {
                text: 'Space (MB)'
            },
            minorTickInterval: 1,
        },
        tooltip: {
            style: {
                zIndex: 9999
            },
            enabled: true,
            formatter: function () {
                return `<div>Database: <b>${this.x}</b></div><br/><div><span style="color:${this.color};">\u25CF</span><span style="font-size:12px;"><b> ${this?.series?.name} </b></span></div><br/> Size: <b>${this.point.y} MB</b>`;
            }
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'top',
            floating: false,
            backgroundColor: 'white',
            shadow: false
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            handleShowDetailsPopup(this.category);
                        }
                    }
                },
                enableMouseTracking: true,
            },
        },
        series: [
            {
                name: "Data File",
                data: databaseData?.y_axis?.data_file,
            },
            {
                name: "Log File",
                data: databaseData?.y_axis?.log_file,
            },
            {
                name: "Total",
                data: databaseData?.y_axis?.total,
            },
        ],
    }

    const handleShowDetailsPopup = (database) => {
        const data = _.filter(databaseData?.api_data, { database_name: database })
        setPopupData(data);
        setShowPopup(true);
    }

    const handleApplyFilters = () => {
        const errorFilters = {
            dbname: filterValues?.dbname || [],
        }
        sessionStorage.setItem("dbsizefilters", encryptData(JSON.stringify(errorFilters)));

        // set counts for filters selected
        let count = 0;
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        getData();
        setIntervalFlag(!intervalFlag);
        setShowFilter(false);
    }

    return useMemo(() => ({
        options, showPopup, setShowPopup, popupData, databaseData, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, handleApplyFilters
    }), [options, showPopup, setShowPopup, popupData, databaseData, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, handleApplyFilters])
}